import { useRouter } from '@bprogress/next';
import { useQueryClient } from '@tanstack/react-query';
import { logEvent } from '@util/analytics';
import { analyticsPromise, AUTH, perf } from '@util/firebase';
import {
  addToDiscoveredProducts,
  createUserDocument,
  sendEmailVerificationOnCall,
  updateUserByKey,
  updateUserPlatforms,
} from '@util/firestore/users';
import { getValidUserDocument } from '@util/firestore/users/user.helpers';
import {
  checkInAppBrowser,
  gateInAppBrowser,
  IN_APP_BROWSER_MESSAGE,
  promptOpenInExternalBrowser,
} from '@util/hooks/checkInAppBrowser';
import { useMyPresence } from '@util/hooks/useMyPresence';
import { OnlineStatus } from '@util/hooks/useOnlineStatus';
import useRealTimeUser from '@util/hooks/useRealtimeUser';
import { getHostUrl } from '@util/index';
import { logError } from '@util/logError';
import { setUserId } from 'firebase/analytics';
import { FirebaseError } from 'firebase/app';
import {
  linkWithCredential as _linkWithCredential,
  sendPasswordResetEmail as _sendPasswordResetEmail,
  ActionCodeOperation,
  ActionCodeSettings,
  applyActionCode,
  AuthErrorCodes,
  checkActionCode,
  confirmPasswordReset,
  createUserWithEmailAndPassword,
  EmailAuthProvider,
  FacebookAuthProvider,
  GoogleAuthProvider,
  linkWithRedirect,
  OAuthProvider,
  onIdTokenChanged,
  PhoneAuthProvider,
  reauthenticateWithCredential,
  RecaptchaVerifier,
  reload,
  signInAnonymously,
  signInWithCredential,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  signInWithRedirect,
  signOut,
  updatePassword,
  User,
  UserCredential,
} from 'firebase/auth';
import { PerformanceTrace, trace } from 'firebase/performance';
import { default as Cookie, default as Cookies } from 'js-cookie';
import { OrderItemDocument } from 'models/order';
import { UserAttribution, UserDocument } from 'models/user';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

// import algoliaInsights from 'search-insights';

let recaptchaVerifierInitPromise: Promise<RecaptchaVerifier> | null = null;

function clearRecaptchaVerifier() {
  recaptchaVerifierInitPromise = null;
  if (typeof window !== 'undefined' && window.recaptchaVerifier) {
    try {
      window.recaptchaVerifier.clear();
    } catch {
      // no-op
    }
    delete window.recaptchaVerifier;
  }
}

async function ensureRecaptchaVerifier(): Promise<RecaptchaVerifier> {
  if (typeof window === 'undefined') {
    throw new Error('RecaptchaVerifier requires a browser environment');
  }

  if (window.recaptchaVerifier) {
    return window.recaptchaVerifier;
  }

  if (!recaptchaVerifierInitPromise) {
    recaptchaVerifierInitPromise = (async () => {
      let recaptchaContainer = document.getElementById('recaptcha-container');

      if (!recaptchaContainer) {
        recaptchaContainer = document.createElement('div');
        recaptchaContainer.id = 'recaptcha-container';
        recaptchaContainer.className = 'sr-only';
        document.body.prepend(recaptchaContainer);
      }

      const recaptchaVerifier = new RecaptchaVerifier(
        AUTH,
        recaptchaContainer,
        { size: 'invisible' }
      );

      await recaptchaVerifier.render();

      window.recaptchaVerifier = recaptchaVerifier;

      return recaptchaVerifier;
    })().finally(() => {
      recaptchaVerifierInitPromise = null;
    });
  }

  return recaptchaVerifierInitPromise;
}

interface AuthProviderProps {
  children: ReactNode;
}

export interface Credentials {
  email: string;
  password: string;
}

export type FirebaseAuthFn<T extends Credentials | AuthProvider> = (
  args: T
) => Promise<FirebaseError | UserCredential>;

export const EMAIL_ERRORS = [
  AuthErrorCodes.USER_DELETED,
  AuthErrorCodes.EMAIL_EXISTS,
  AuthErrorCodes.INVALID_EMAIL,
  AuthErrorCodes.UNVERIFIED_EMAIL,
  AuthErrorCodes.INVALID_RECIPIENT_EMAIL,
];

export const PASSWORD_ERRORS = [
  AuthErrorCodes.WEAK_PASSWORD,
  AuthErrorCodes.INVALID_PASSWORD,
];

type AuthProvider = 'google' | 'facebook' | 'apple';

interface AuthContext {
  user: User | null | undefined;
  userDoc?: UserDocument | null;
  refreshUserDoc: () => void;
  isLoadingUserDoc: boolean;
  isLoggingIn: boolean;
  logout: (options?: { clearCache?: boolean }) => Promise<void>;
  loginEmailPassword: FirebaseAuthFn<Credentials>;
  // loginSocial: FirebaseAuthFn<AuthProvider>;
  loginSocialRedirect: (args: AuthProvider) => Promise<void | FirebaseError>;
  createAccountEmailPassword: FirebaseAuthFn<Credentials>;
  signInWithToken: FirebaseAuthFn<Credentials>;
  linkSocialToCredential: (
    providerName: AuthProvider
  ) => Promise<{ user: User } | undefined>;
  sendPasswordResetEmail: (email: string) => Promise<void>;
  resetPassword: (oobCode: string, password: string) => Promise<void>;
  updateUserPassword: (oldPassword: string, password: string) => Promise<void>;
  isAccountEmailPassword: () => boolean;
  sendEmailVerification: (
    newEmail: string
  ) => Promise<{ status: 'already_verified' | 'current_email' | 'new_email' }>;
  verifyEmailByCode: (
    oobCode: string
  ) => Promise<{ status: 'current_email' | 'new_email' }>;
  sendPhoneVerification: (phoneNumber: string) => Promise<string>;
  signInWithPhone: (
    id: string,
    code: string
  ) => Promise<UserCredential | FirebaseError>;
  linkPhone: (id: string, code: string) => Promise<UserCredential>;
  showWelcomeBackModal: boolean;
  setShowWelcomeBackModal: (show: boolean) => void;
  observeProduct: ({
    id,
    attribution,
  }: {
    id: string;
    attribution: OrderItemDocument['attribution'];
  }) => void;
  signInAnon: () => Promise<{
    anonAuthenticated: boolean;
    userDocCreated: boolean;
  }>;
  attribution: UserAttribution | null;
  getUserPhoneNumber: () => Promise<string | null>;
  startPerfTrace: (name: string) => {
    stop: (attributes?: Record<string, string | number>) => number;
  };
}
const AuthContext = createContext({} as AuthContext);

export const initAuthProvider = (p: AuthProvider) => {
  if (p === 'apple') return new OAuthProvider('apple.com');
  if (p === 'facebook') return new FacebookAuthProvider();
  return new GoogleAuthProvider();
};

const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null | undefined>();
  const userRef = useRef(user);
  userRef.current = user;
  const signInAnonInFlightRef = useRef<Promise<{
    anonAuthenticated: boolean;
    userDocCreated: boolean;
  }> | null>(null);
  const [showWelcomeBackModal, setShowWelcomeBackModal] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  // Runs once per uid, right after the user's presence connects for the first
  // time. `previousStatus` is read before the online write overwrites it.
  const onPresenceFirstConnect = useCallback(
    (previousStatus: OnlineStatus | null, uid: string) => {
      const lastChanged = previousStatus?.last_changed;
      const fourteenDaysAgo = Date.now() - 1000 * 60 * 60 * 24 * 14;
      if (lastChanged && lastChanged < fourteenDaysAgo) {
        logEvent('welcome_back_modal_shown', { uid });
        setShowWelcomeBackModal(true);
      }
      updateUserPlatforms(uid);
      void analyticsPromise()
        .then((analytics) => {
          if (analytics) setUserId(analytics, uid);
        })
        .catch((e) => {
          logError(e, 'onPresenceFirstConnect.analytics');
        });
    },
    []
  );
  const { setOffline } = useMyPresence(user?.uid, onPresenceFirstConnect);
  const queryClient = useQueryClient();
  const router = useRouter();
  // Performance tracing helper for auth/login flows
  const startPerfTrace = (name: string) => {
    let t: PerformanceTrace | null = null;
    const startedAt = Date.now();
    if (perf) {
      try {
        t = trace(perf, name);
        t.start();
      } catch {}
    }
    return {
      stop: (attributes?: Record<string, string | number>) => {
        const durationMs = Date.now() - startedAt;
        if (t) {
          if (attributes) {
            Object.entries(attributes).forEach(([key, value]) => {
              try {
                t!.putAttribute(key, String(value));
              } catch {}
            });
          }
          try {
            t.stop();
          } catch {}
        }
        if (process.env.NODE_ENV === 'development') {
          console.log(`${name} took ${durationMs}ms`, attributes);
        }
        if (durationMs > 10000) {
          logError(
            new Error('Slow auth trace'),
            undefined,
            undefined,
            `${name} took ${durationMs}ms`
          );
        }
        return durationMs;
      },
    };
  };

  const [attribution, setAttribution] = useState<UserAttribution | null>(null);
  // attribution tracker
  useEffect(() => {
    if (attribution) return;
    const params = new URLSearchParams(window.location.search);
    const source = params.get('utm_source');
    const medium = params.get('utm_medium');
    const campaign = params.get('utm_campaign');
    const term = params.get('utm_term');
    const content = params.get('utm_content');
    const id = params.get('utm_id');
    const fbclid = params.get('fbclid');
    if (source) {
      setAttribution({
        source,
        medium,
        campaign,
        term,
        content,
        id,
        fbclid,
      });
    }
  }, [attribution]);

  useEffect(() => {
    return () => {
      try {
        clearRecaptchaVerifier();
      } catch (e) {
        logError(e, 'recaptchaVerifier cleanup');
      }
    };
  }, []);

  // get user document
  const [userDocRefreshKey, setUserDocRefreshKey] = useState(0);
  const userDoc = useRealTimeUser(user?.uid, userDocRefreshKey);
  const refreshUserDoc = useCallback(() => {
    setUserDocRefreshKey((key) => key + 1);
  }, []);
  const isLoggingOutRef = useRef(false);
  const logout = useCallback(
    async (options?: { clearCache?: boolean }) => {
      if (isLoggingOutRef.current) return;
      isLoggingOutRef.current = true;
      try {
        router.replace('/');
        const impersonated = Cookies.get('impersonated');
        if (!impersonated) await setOffline();
        try {
          await signOut(AUTH);
        } catch (e) {
          // user deleted account but we still want to clear user state
          logError(e, 'logout');
        }
        setUser(null);
        if (options?.clearCache) {
          queryClient.clear();
        }
        Cookie.remove('auth');
        Cookie.remove('algoliaUserToken');
        Cookie.remove('impersonated');
      } finally {
        isLoggingOutRef.current = false;
      }
    },
    [setOffline, queryClient, router]
  );
  useEffect(() => {
    if (userDoc?.banned) {
      void logout({ clearCache: true });
    }
  }, [logout, userDoc?.banned]);

  const sendPhoneVerification = async (phoneNumber: string) => {
    const t = startPerfTrace('auth_sendPhoneVerification');
    if (typeof window === 'undefined') return '';
    // Reuse a single verifier so we don't render twice in the same container:
    // https://stackoverflow.com/questions/54110489/recaptcha-has-already-been-rendered-in-this-element
    const recaptchaVerifier = await ensureRecaptchaVerifier();
    const provider = new PhoneAuthProvider(AUTH);
    const verificationId = await provider.verifyPhoneNumber(
      phoneNumber,
      recaptchaVerifier
    );
    t.stop();
    return verificationId;
  };

  const signInWithPhone = async (verificationId: string, code: string) => {
    const t = startPerfTrace('auth_signInWithPhone');
    try {
      const credential = PhoneAuthProvider.credential(verificationId, code);
      const userCredential = await signInWithCredential(AUTH, credential);
      setUser(userCredential.user);
      t.stop();
      return userCredential;
    } catch (e) {
      t.stop({ error: 'true' });
      return handleAuthError(e);
    }
  };

  const linkPhone = async (
    verificationId: string,
    verificationCode: string
  ) => {
    const t = startPerfTrace('auth_linkPhone');
    if (!AUTH.currentUser)
      throw new Error('An error occurred. Please try again.');
    try {
      const authCredential = PhoneAuthProvider.credential(
        verificationId,
        verificationCode
      );
      const cred = await _linkWithCredential(AUTH.currentUser, authCredential);
      t.stop();
      return cred;
    } catch (e) {
      t.stop({ error: 'true' });
      const err = e as FirebaseError;

      logError(err);

      switch (err.code) {
        case 'auth/invalid-verification-code':
          throw new Error('Invalid verification code');
        case 'auth/missing-verification-code':
          throw new Error('Missing verification code');
        case 'auth/code-expired':
          throw new Error('Verification code expired');
        case 'auth/credential-already-in-use':
          throw new Error('Phone number already in use');
        case 'auth/account-exists-with-different-credential':
          throw new Error('Phone number already in use');
        default:
          throw new Error('An unknown error occurred. Please try again.');
      }
    }
  };

  const getUserPhoneNumber = async () => {
    if (!AUTH.currentUser) throw new Error('No AUTH.currentUser');
    // Check if phone number exists in Firebase Auth user
    if (AUTH.currentUser.phoneNumber) return AUTH.currentUser.phoneNumber;
    // If not in auth, check if it's in the user document

    const userDoc = getValidUserDocument(
      AUTH.currentUser.uid,
      AUTH.currentUser.email ?? '',
      {
        name: AUTH.currentUser.displayName ?? 'Anonymous User',
        opted_out: false,
      },
      attribution
    );

    return userDoc?.phone || null;
  };

  const sendEmailVerification = async (
    newEmail: string
  ): Promise<{
    status: 'already_verified' | 'current_email' | 'new_email';
  }> => {
    const currentUser = AUTH.currentUser;
    if (!currentUser) throw new Error('No current user');
    const normalizedNewEmail = newEmail
      ? newEmail.trim().toLowerCase()
      : undefined;
    if (!normalizedNewEmail) throw new Error('No email input');
    const status = await sendEmailVerificationOnCall(normalizedNewEmail);
    return { status };
  };

  const verifyEmailByCode = async (
    oobCode: string
  ): Promise<{ status: 'current_email' | 'new_email' }> => {
    try {
      const actionInfo = await checkActionCode(AUTH, oobCode);
      if (
        actionInfo.operation !== ActionCodeOperation.VERIFY_EMAIL &&
        actionInfo.operation !== ActionCodeOperation.VERIFY_AND_CHANGE_EMAIL
      ) {
        throw new Error(`Unexpected email action: ${actionInfo.operation}`);
      }
      const targetEmail = actionInfo.data.email
        ? actionInfo.data.email.trim().toLowerCase()
        : undefined;

      await applyActionCode(AUTH, oobCode);

      const isEmailChange =
        actionInfo.operation === ActionCodeOperation.VERIFY_AND_CHANGE_EMAIL;

      const currentUser = AUTH.currentUser;
      // firebase doesn't need a currentUser to apply the code (so this is optional)
      if (currentUser) {
        if (isEmailChange) {
          // Changing the auth email revokes the session's refresh tokens
          // (Firebase security behavior), so reload()/getIdToken(true) would
          // throw auth/user-token-expired. Sign out cleanly instead; the
          // backend syncs the new email to the user document.
          try {
            await signOut(AUTH);
          } catch (e) {
            logError(e, 'verifyEmailByCode.signOut');
          }
        } else {
          try {
            await reload(currentUser);
            await currentUser.getIdToken(true);
            const refreshedEmail = currentUser.email
              ? currentUser.email.trim().toLowerCase()
              : undefined;
            if (refreshedEmail && refreshedEmail === targetEmail) {
              // update email on user document (not important as backend will resolve eventually)
              await updateUserByKey(currentUser.uid, 'email', refreshedEmail);
            } else {
              // silently fail - we'll update in backend
              logError(
                `Email mismatch: ${refreshedEmail} !== ${targetEmail}`,
                'verifyEmailByCode.email_mismatch'
              );
            }
          } catch (e) {
            logError(e, 'verifyEmailByCode.reload');
          }
        }
      }
      return { status: isEmailChange ? 'new_email' : 'current_email' };
    } catch (error) {
      logError(error, 'verifyEmailByCode');
      throw new Error(
        'This verification link is invalid, expired, or has already been used.'
      );
    }
  };

  const linkSocialToCredential = async (providerName: AuthProvider) => {
    if (!AUTH.currentUser) throw new Error('No AUTH.currentUser');
    const provider = initAuthProvider(providerName);

    try {
      await linkWithRedirect(AUTH.currentUser, provider);

      // Will be handled in backend
      // if (result && AUTH.currentUser) {
      //   // update the user's email to the email they used to sign in with
      //   updateUserEmail(result.user.email ?? '');

      //   return result;
      // }
    } catch (error) {
      const err = error as FirebaseError;

      if (err.code === 'auth/provider-already-linked') {
        return {
          user: AUTH.currentUser,
        };
      }

      throw error;
    }
  };

  useEffect(() => {
    if (user === undefined) return;
    // const isEmailLink = isSignInWithEmailLink(AUTH, window.location.href);
    // if (!isEmailLink) return;
    const params = new URLSearchParams(window.location.search);
    const customToken = params?.get('mxt');
    if (!customToken) return;
    const decodedCustomToken = decodeURIComponent(customToken);
    signInWithCustomToken(AUTH, decodedCustomToken)
      .catch((e) => {
        logError(
          e,
          'signInWithCustomToken',
          undefined,
          'Failed to sign in with custom token: ' + customToken
        );
        window.location.href = `/authwall?mxt=${customToken}&continueUrl=${
          window.location.href.split('?')[0]
        }`;
      })
      .then(() => {
        // remove mxt from url
        const url = new URL(window.location.href);
        url.searchParams.delete('mxt');
        window.history.replaceState({}, '', url.toString());
      });
  }, [user]);

  useEffect(() => {
    const unsubscribe = onIdTokenChanged(AUTH, (user) => {
      // set user from email/password login
      setUser(user);
      if (user) {
        user.getIdToken().then((token) => {
          Cookie.set('auth', token, { expires: 1 });
        });
        Cookie.set('uid', user.uid);
        // remove klayvio script if user is logged in
        // const klaviyoScript = document.getElementById('klaviyo-popup');
        // if (klaviyoScript) {
        //   klaviyoScript.remove();
        // }
      } else if (user === null) {
        Cookie.remove('auth');
        Cookie.remove('uid');
        setUser(null);
      }
    });

    return () => {
      unsubscribe();
    };
  }, [queryClient]);

  const handleAuthError = (e: unknown) => {
    if (e instanceof FirebaseError) {
      return e;
    }
    logError(e);
    throw new Error('Encountered unknown error in auth flow');
  };

  // deprecated in favor of loginSocialRedirect
  // popups are sometimes blocked by browsers
  // const loginSocial: FirebaseAuthFn<AuthProvider> = async (args) => {
  //   const t = startPerfTrace('auth_loginSocial');
  //   try {
  //     const inApp = checkInAppBrowser();
  //     if (inApp)
  //       throw new FirebaseError(
  //         'auth/invalid-browser',
  //         'Invalid Browser. Please open in this page in your default browser.'
  //       );
  //     setIsLoggingIn(true);
  //     const provider = initAuthProvider(args);
  //     if (isMobile()) {
  //       await setPersistence(AUTH, browserLocalPersistence);
  //     }
  //     const result = await signInWithPopup(AUTH, provider);

  //     if (!result) {
  //       throw new FirebaseError('auth/unknown', 'Unknown error');
  //     }

  //     setUser(result?.user);
  //     setIsLoggingIn(false);
  //     t.stop({ provider: args });
  //     return result;
  //   } catch (e) {
  //     setIsLoggingIn(false);
  //     t.stop({ provider: args, error: 'true' });
  //     return handleAuthError(e);
  //   }
  // };

  const loginSocialRedirect = async (args: AuthProvider) => {
    const t = startPerfTrace('auth_loginSocialRedirect');
    try {
      setIsLoggingIn(true);
      if (checkInAppBrowser()) {
        const openedNewWindow = promptOpenInExternalBrowser();
        setIsLoggingIn(false);
        t.stop({
          provider: args,
          openedNewWindow: openedNewWindow ? 'true' : 'false',
        });
        if (!openedNewWindow) {
          return new FirebaseError(
            'auth/invalid-browser',
            IN_APP_BROWSER_MESSAGE
          );
        }
        return;
      }
      const provider = initAuthProvider(args);
      await signInWithRedirect(AUTH, provider);
      setIsLoggingIn(false);
      t.stop({ provider: args });
    } catch (e) {
      setIsLoggingIn(false);
      t.stop({ provider: args, error: 'true' });
      return handleAuthError(e);
    }
  };

  const loginEmailPassword: FirebaseAuthFn<Credentials> = async (args) => {
    const t = startPerfTrace('auth_loginEmailPassword');
    try {
      setIsLoggingIn(true);
      const userCredential = await signInWithEmailAndPassword(
        AUTH,
        args.email,
        args.password
      );
      setUser(userCredential.user);
      setIsLoggingIn(false);
      t.stop();
      return userCredential;
    } catch (e) {
      setIsLoggingIn(false);
      t.stop({ error: 'true' });
      return handleAuthError(e);
    }
  };

  // admin feature
  const signInWithToken: FirebaseAuthFn<Credentials> = async (args) => {
    const t = startPerfTrace('auth_signInWithToken');
    try {
      setIsLoggingIn(true);
      const userCredential = await signInWithCustomToken(AUTH, args.email);
      setUser(userCredential.user);
      setIsLoggingIn(false);
      t.stop();
      return userCredential;
    } catch (e) {
      setIsLoggingIn(false);
      t.stop({ error: 'true' });
      return handleAuthError(e);
    }
  };

  const createAccountEmailPassword: FirebaseAuthFn<Credentials> = async (
    args
  ) => {
    try {
      const userCredential = await createUserWithEmailAndPassword(
        AUTH,
        args.email,
        args.password
      );
      return userCredential;
    } catch (e) {
      return handleAuthError(e);
    }
  };

  const sendPasswordResetEmail = async (email: string) => {
    const actionCodeSettings: ActionCodeSettings = {
      url: `${getHostUrl()}/reset-password`,
    };
    await _sendPasswordResetEmail(AUTH, email, actionCodeSettings);
  };

  const resetPassword = async (oobCode: string, password: string) => {
    await confirmPasswordReset(AUTH, oobCode, password);
  };

  const updateUserPassword = async (
    oldPassword: string,
    newPassword: string
  ) => {
    if (AUTH.currentUser?.email && isAccountEmailPassword()) {
      const credential = EmailAuthProvider.credential(
        AUTH.currentUser.email,
        oldPassword
      );
      try {
        await reauthenticateWithCredential(AUTH.currentUser, credential);
        await updatePassword(AUTH.currentUser, newPassword);
      } catch (err) {
        throw new Error('Incorrect password');
      }
    }
  };

  const isAccountEmailPassword = () => {
    if (AUTH.currentUser) {
      return AUTH.currentUser.providerData.some(
        (p) => p.providerId === 'password'
      );
    }
    return false;
  };

  const signInAnon = async (): Promise<{
    anonAuthenticated: boolean;
    userDocCreated: boolean;
  }> => {
    if (signInAnonInFlightRef.current) return signInAnonInFlightRef.current;

    const run = async (): Promise<{
      anonAuthenticated: boolean;
      userDocCreated: boolean;
    }> => {
      const inAppGate = gateInAppBrowser(
        'Please open this page in Safari or Chrome to add items to your cart.'
      );
      if (inAppGate === 'opened_external')
        return { anonAuthenticated: false, userDocCreated: false };

      const ANON_SIGN_IN_TIMEOUT_MS = 10_000;
      const deadline = Date.now() + ANON_SIGN_IN_TIMEOUT_MS;

      // Wait for onIdTokenChanged to settle (user !== undefined)
      while (userRef.current === undefined && Date.now() < deadline) {
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      if (AUTH.currentUser?.isAnonymous) {
        setUser(AUTH.currentUser);
        return { anonAuthenticated: true, userDocCreated: false };
      }

      // Try client setDoc for faster avatar; auth trigger is the fallback.
      let anonUser: User | undefined;
      let lastError: unknown;
      let userDocCreated = false;

      while (!anonUser && Date.now() < deadline) {
        try {
          const credential = await signInAnonymously(AUTH);
          anonUser = credential.user;
          setUser(anonUser);

          try {
            await createUserDocument(
              getValidUserDocument(
                anonUser.uid,
                anonUser.email ?? '',
                {
                  name: 'Anonymous User',
                  opted_out: false,
                },
                attribution
              ),
              'anonymous'
            );
            userDocCreated = true;
          } catch (error) {
            logError(
              error,
              'signInAnon',
              undefined,
              'Failed to create user document'
            );
            // its ok if this fails - backend authTrigger will create it
            // we just need to refresh the user doc later
            userDocCreated = false;
          }
        } catch (error) {
          lastError = error;
          await new Promise((resolve) => setTimeout(resolve, 400));
        }
      }

      // fallback to firebase admin auth
      if (!anonUser) {
        try {
          const res = await fetch('/api/guest-session', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(attribution ? { attribution } : {}),
          });
          const body = (await res.json()) as {
            customToken?: string;
            userDocCreated?: boolean;
            error?: string;
          };
          if (!res.ok || !body.customToken)
            throw new Error(body.error ?? 'Guest session failed');

          const credential = await signInWithCustomToken(
            AUTH,
            body.customToken
          );
          anonUser = credential.user;
          userDocCreated = body.userDocCreated ?? false;
          setUser(anonUser);
        } catch (error) {
          const clientDetail =
            lastError instanceof FirebaseError
              ? `${lastError.code}: ${lastError.message}`
              : lastError instanceof Error
              ? lastError.message
              : 'Unknown client error';
          const serverDetail =
            error instanceof Error ? error.message : 'Unknown server error';
          logError(
            lastError ?? error,
            'signInAnon',
            undefined,
            `Anonymous sign-in failed (client: ${clientDetail}, server fallback: ${serverDetail})`
          );
          throw new Error(`Auth Failed: ${serverDetail}`);
        }
      }

      return { anonAuthenticated: !!anonUser, userDocCreated };
    };

    const promise = run();
    signInAnonInFlightRef.current = promise;
    try {
      const { anonAuthenticated, userDocCreated } = await promise;
      if (userDocCreated) refreshUserDoc();
      return { anonAuthenticated, userDocCreated };
    } finally {
      signInAnonInFlightRef.current = null;
    }
  };

  const observedProducts = useRef<
    {
      id: string;
      attribution: OrderItemDocument['attribution'];
    }[]
  >([]);
  const sentDiscoveredProductIds = useRef<Set<string>>(new Set());
  const observerTimeout = useRef<any | null>(null);
  const observeProduct = ({
    id,
    attribution,
  }: {
    id: string;
    attribution: OrderItemDocument['attribution'];
  }) => {
    // One impression per product per search: the same listing shown by a second
    // search has a different position, which is what rank analysis needs.
    const seenKey = attribution.search_id
      ? `${id}:${attribution.search_id}`
      : id;
    if (
      !observedProducts.current.some(
        (p) => p.id === id && p.attribution.search_id === attribution.search_id
      ) &&
      !sentDiscoveredProductIds.current.has(seenKey)
    ) {
      observedProducts.current.push({
        id,
        attribution,
      });
      sentDiscoveredProductIds.current.add(seenKey);
    }
    if (observerTimeout.current) {
      clearTimeout(observerTimeout.current);
    }
    observerTimeout.current = setTimeout(() => {
      addToDiscoveredProducts(observedProducts.current);
      observedProducts.current = [];
    }, 1000);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        userDoc,
        refreshUserDoc,
        isLoadingUserDoc: userDoc === undefined,
        isLoggingIn,
        logout,
        loginEmailPassword,
        // loginSocial,
        loginSocialRedirect,
        signInWithToken,
        createAccountEmailPassword,
        sendEmailVerification,
        linkSocialToCredential,
        sendPasswordResetEmail,
        resetPassword,
        updateUserPassword,
        isAccountEmailPassword,
        verifyEmailByCode,
        sendPhoneVerification,
        signInWithPhone,
        linkPhone,
        showWelcomeBackModal,
        setShowWelcomeBackModal,
        observeProduct,
        signInAnon,
        attribution,
        getUserPhoneNumber,
        startPerfTrace,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth() {
  return useContext(AuthContext);
}

export default AuthProvider;
